import { map, values } from 'lodash-es'
import type { MetadataUpdate } from '#app/lib/metadata/update'
import { treq, treqPartial } from '#app/lib/treq'
import { getEntitiesAttributesByUris, getEntitiesImagesUrls, serializeEntities, type SerializedEntity } from '#entities/lib/entities'
import { addEntitiesImages } from '#entities/lib/types/work_alt'
import { askConfirmation } from '#general/lib/confirmation_modal'
import type { RelativeUrl } from '#server/types/common'
import type { ControllersAPI } from '#server/types/controllers_api'
import type { ListingElement, ListingElementId } from '#server/types/element'
import type { EntityUri } from '#server/types/entity'
import type { Listing, ListingId, ListingWithElements } from '#server/types/listing'
import type { UserId } from '#server/types/user'
import { getCurrentLang, I18n, i18n } from '#user/lib/i18n'
import { getUserById } from '#users/users_data'

export interface ListingElementWithEntity extends ListingElement {
  entity: SerializedEntity
}

export async function getListingWithElementsById (id: ListingId, limit?: number, offset?: number) {
  const { list: listing } = await treq('lists', 'get', 'by-id', { id, limit, offset })
  return serializeListing(listing)
}

export async function getListingElementById (id: ListingElementId) {
  const { element, list: listing } = await treq('lists', 'get', 'by-element-id', { id })
  return { element, listing }
}

export interface ListingByCreatorsParams {
  usersIds: UserId[]
  withElements?: boolean
  offset?: number
  limit?: number
}

export async function getListingsByCreators ({ usersIds, withElements, offset, limit }: ListingByCreatorsParams) {
  if (usersIds.length === 0) return { listings: [] as Listing[] }
  const { lists: listings } = await treq('lists', 'get', 'by-creators', {
    users: usersIds,
    'with-elements': withElements,
    offset,
    limit,
  })
  return { listings }
}

export async function createListing (list: Pick<Listing, 'name' | 'description' | 'visibility' | 'type'>) {
  const { list: listing } = await treq('lists', 'post', 'default', list)
  return { listing }
}

export const deleteListing = treqPartial('lists', 'post', 'delete')

export async function getListingsByEntityUri (uri: EntityUri) {
  const { lists } = await treq('lists', 'get', 'by-entities', { uris: [ uri ] })
  return lists
}

export async function getUserListingsByEntityUri ({ userId, uri }: { userId: UserId, uri: EntityUri }) {
  const { listings } = await getListingsByCreators({ usersIds: [ userId ] })
  const listingsIds = map(listings, '_id')
  return getListingsContainingEntityUri({ listingsIds, uri })
}

async function getListingsContainingEntityUri ({ listingsIds, uri }: { listingsIds: ListingId[], uri: EntityUri }) {
  if (listingsIds.length === 0) return []
  const { lists } = await treq('lists', 'get', 'by-entities', { uris: [ uri ], lists: listingsIds })
  return lists
}

export async function getElementByUri ({ listingId, uri }: { listingId: ListingId, uri: EntityUri }) {
  const listing = await getListingsContainingEntityUri({ listingsIds: [ listingId ], uri })
  const { elements } = listing[0]
  return elements.find(el => el.uri === uri)
}

export async function updateListing (list: ControllersAPI['lists']['put']['default']['params']) {
  const { list: listing } = await treq('lists', 'put', 'default', list)
  return { listing }
}

export async function addElement (id: ListingId, uri: EntityUri) {
  return treq('lists', 'put', 'add-elements', { id, uris: [ uri ] })
}

export async function removeElement (id: ListingId, uri: EntityUri) {
  return treq('lists', 'put', 'remove-elements', { id, uris: [ uri ] })
}

export async function updateElement (params: ControllersAPI['lists']['put']['update-element']['params']) {
  try {
    const res = treq('lists', 'put', 'update-element', params)
    return res
  } catch (err) {
    if (err.message !== 'nothing to update') {
      throw err
    }
  }
}

interface SerializedListingExtras {
  pathname: RelativeUrl
  checked?: boolean
}

export type SerializedListing = Listing & SerializedListingExtras

export type SerializedListingWithElements = SerializedListing & ListingWithElements

export function serializeListing <T extends Listing | ListingWithElements> (listing: T): T & SerializedListingExtras {
  const { _id: id } = listing
  return Object.assign(listing, {
    pathname: getListingPathname(id),
  })
}

export async function countListings (userId: UserId) {
  const { listings } = await getListingsByCreators({ usersIds: [ userId ] })
  return Object.keys(listings).length
}

export const getListingPathname = (id: ListingId) => `/lists/${id}` as RelativeUrl

export function getElementPathname (listingId: ListingId, elementId: ListingElementId) {
  return `/lists/${listingId}/element/${elementId}`
}

export async function getListingMetadata (listing: Listing) {
  return {
    title: await getListingLongTitle(listing),
    description: listing.description,
    url: getListingPathname(listing._id),
    smallCardType: true,
  }
}

export async function getElementMetadata (listing: Listing, element: ListingElementWithEntity) {
  return {
    title: await getElementTitle(listing, element),
    url: getElementPathname(listing._id, element._id),
    smallCardType: true,
  } as MetadataUpdate
}

async function getListingLongTitle (listing: Listing) {
  const { name, creator } = listing
  const { username } = await getUserById(creator)
  return `${name} - ${i18n('list_created_by', { username })}`
}

async function getElementTitle (listing: Listing, element: ListingElementWithEntity) {
  const { entity } = element
  const { name, creator } = listing
  const { username } = await getUserById(creator)
  return `${entity.title} - ${name} - ${i18n('list_created_by', { username })}`
}

export async function askUserConfirmationAndRemove (removeElement: () => void, deletingData: string) {
  if (deletingData) {
    askConfirmation({
      confirmationText: i18n('Are you sure you want to **delete this element**? That will also delete the following text: %{deletingData}…', { deletingData: deletingData.slice(0, 50) }),
      warningText: I18n('cant_undo_warning'),
      action: removeElement,
    })
  } else {
    await removeElement()
  }
}

export async function assignEntitiesToElements (elements: ListingElement[]) {
  const uris = map(elements, 'uri')
  const res = await getEntitiesAttributesByUris({
    uris,
    attributes: [ 'info', 'labels', 'claims', 'image' ],
    lang: getCurrentLang(),
  })
  const entitiesByUris = res.entities
  const entities = await serializeEntities(values(entitiesByUris))
  await addEntitiesImages(entities)
  return elements.map(element => {
    // @ts-expect-error
    element.entity = entitiesByUris[element.uri]
    return element
  }) as ListingElementWithEntity[]
}

export async function getElementsImages (elements: ListingElement[], imagesLimit: number) {
  const allElementsUris = map(elements, 'uri')

  let imagesUrls = []
  let limit = 0
  async function fetchMoreImages (offset = 0, amount = 10) {
    const enoughImages = imagesUrls.length >= imagesLimit
    if (enoughImages) return
    limit = offset + 10
    const elementsUris = allElementsUris.slice(offset, limit)
    const someImagesUrls = await getEntitiesImagesUrls(elementsUris)
    imagesUrls = [ ...imagesUrls, ...someImagesUrls ]
    if (elementsUris.length === 0) return
    offset = amount
    amount += amount
    return fetchMoreImages(offset, amount)
  }
  await fetchMoreImages()
  return imagesUrls
}

export async function getListingElementsImages (listing: SerializedListing | SerializedListingWithElements, imagesLimit: number) {
  let elements
  if ('elements' in listing) {
    ;({ elements } = listing)
  } else {
    // Requesting more elements than might be needed in case the first elements don't have an image
    ;({ elements } = await getListingWithElementsById(listing._id, imagesLimit * 4))
  }
  return getElementsImages(elements, imagesLimit)
}
